import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Leaf, Cpu, Printer, FileText, Wrench, Search } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import NewsletterSignup from "@/components/NewsletterSignup";

const BRANDS = ["HP", "Brother", "Canon", "Epson", "Xerox", "Samsung", "Lexmark", "Ricoh", "Kyocera", "OKI"];

const CAT_ICONS = {
    "toners-compativeis": Printer,
    "toners-originais": Printer,
    "tinteiros": FileText,
    "impressoras-recondicionadas": Printer,
    "computadores-recondicionados": Cpu,
    "assistencia-tecnica": Wrench,
};

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [categories, setCategories] = useState([]);
    const [settings, setSettings] = useState(null);
    const [printerQ, setPrinterQ] = useState("");
    const [printerSugg, setPrinterSugg] = useState([]);
    const [showSugg, setShowSugg] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/products?is_featured=true&limit=8").then((r) => setFeatured(r.data));
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/settings").then((r) => setSettings(r.data));
    }, []);

    useEffect(() => {
        if (!printerQ || printerQ.length < 2) {
            setPrinterSugg([]);
            return;
        }
        const t = setTimeout(() => {
            api.get(`/printers/suggest?q=${encodeURIComponent(printerQ)}`).then((r) => setPrinterSugg(r.data));
        }, 250);
        return () => clearTimeout(t);
    }, [printerQ]);

    const goToPrinter = (model) => {
        nav(`/loja?printer=${encodeURIComponent(model)}`);
    };

    const heroTitle = settings?.hero_title || "Imprima mais.";
    const heroAccent = settings?.hero_title_accent || "Pague menos.";
    const heroSub = settings?.hero_subtitle || "Consumíveis informáticos compatíveis e originais, impressoras e computadores recondicionados com garantia.";

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b-2 border-ink">
                <div className="grid-bg absolute inset-0 pointer-events-none" />
                <div className="container-edge grid lg:grid-cols-12 gap-10 py-16 md:py-24 relative">
                    <div className="lg:col-span-7 flex flex-col justify-center animate-fade-in-up">
                        <div className="text-sm md:text-base font-mono uppercase tracking-[0.28em] text-magenta mb-8">
                            Toners · Tinteiros · Impressoras · Computadores
                        </div>
                        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tight leading-[1.02]">
                            {heroTitle}
                            <br />
                            <span className="text-magenta font-medium">{heroAccent}</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base md:text-lg text-ink/70 leading-relaxed">
                            {heroSub}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/loja" className="btn-primary" data-testid="hero-shop-cta">
                                Ver Catálogo <ArrowRight size={18} />
                            </Link>
                            <Link to="/loja/assistencia-tecnica" className="btn-secondary">
                                Pedir Assistência
                            </Link>
                        </div>

                        {/* Printer search box */}
                        <div className="mt-10 border-2 border-ink bg-white p-5 max-w-xl" data-testid="hero-printer-search">
                            <div className="label-mono text-magenta mb-3 inline-flex items-center gap-2">
                                <Search size={14} /> Encontre o consumível para a sua impressora
                            </div>
                            <form
                                onSubmit={(e) => { e.preventDefault(); if (printerQ.trim()) goToPrinter(printerQ.trim()); }}
                                className="relative"
                            >
                                <div className="flex">
                                    <input
                                        value={printerQ}
                                        onChange={(e) => { setPrinterQ(e.target.value); setShowSugg(true); }}
                                        onFocus={() => setShowSugg(true)}
                                        onBlur={() => setTimeout(() => setShowSugg(false), 200)}
                                        placeholder="ex: HP LaserJet 1010, Brother HL-L2350, Canon MF 4120…"
                                        className="input-edge flex-1 border-r-0"
                                        data-testid="hero-printer-input"
                                    />
                                    <button
                                        type="submit"
                                        className="bg-magenta text-white border-2 border-magenta px-5 hover:bg-ink hover:border-ink transition-colors font-bold"
                                        data-testid="hero-printer-btn"
                                    >
                                        <Search size={18} />
                                    </button>
                                </div>
                                {showSugg && printerSugg.length > 0 && (
                                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border-2 border-ink shadow-[6px_6px_0_0_#0A0A0A] max-h-72 overflow-y-auto">
                                        {printerSugg.slice(0, 8).map((s) => (
                                            <button
                                                key={s.model}
                                                type="button"
                                                onMouseDown={() => goToPrinter(s.model)}
                                                className="block w-full text-left px-4 py-3 text-sm hover:bg-magenta hover:text-white border-b border-ink/10 last:border-b-0"
                                            >
                                                <span className="font-mono">{s.model}</span>
                                                <span className="ml-2 text-xs text-ink/50">{s.count} consumíveis</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </form>
                        </div>
                        <div className="mt-10 grid grid-cols-3 gap-6 pt-8 border-t-2 border-ink max-w-xl">
                            {[
                                { v: "+5.000", l: "Produtos em catálogo" },
                                { v: "24h", l: "Despacho expresso" },
                                { v: "24m", l: "Garantia" },
                            ].map((s) => (
                                <div key={s.l}>
                                    <div className="font-display font-black text-2xl">{s.v}</div>
                                    <div className="label-mono text-ink/60 mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative border-2 border-ink bg-white aspect-square overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?auto=format&fit=crop&w=1000&q=80"
                                alt="Toners e impressoras"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-ink text-white p-5">
                                <div className="label-mono text-magenta">Mais vendido</div>
                                <div className="font-display font-bold text-xl mt-1">
                                    Toner Compatível HP CF283A
                                </div>
                                <div className="font-mono text-sm mt-1 text-white/70">
                                    Desde 14,90€ · Em stock
                                </div>
                            </div>
                        </div>
                        <div className="absolute -top-4 -right-4 bg-magenta text-white border-2 border-ink p-4 rotate-3 shadow-[6px_6px_0_0_#0A0A0A] hidden md:block">
                            <div className="font-display font-black text-3xl leading-none">-65%</div>
                            <div className="label-mono">Vs original</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BRANDS MARQUEE */}
            <section className="border-b-2 border-ink bg-white py-6 overflow-hidden">
                <div className="flex items-center gap-12 whitespace-nowrap animate-marquee">
                    {[...BRANDS, ...BRANDS, ...BRANDS].map((b, i) => (
                        <span
                            key={i}
                            className="font-display font-medium text-2xl md:text-3xl tracking-tight text-ink/60 hover:text-magenta"
                        >
                            {b}
                        </span>
                    ))}
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="container-edge py-16 md:py-24" data-testid="categories-section">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <div className="label-mono text-magenta">01 — Categorias</div>
                        <h2 className="font-sans font-light text-4xl md:text-5xl tracking-tight mt-2">
                            Tudo o que precisa
                            <br />
                            num só sítio.
                        </h2>
                    </div>
                    <Link to="/loja" className="btn-ghost">
                        Ver todos os produtos <ArrowRight size={16} />
                    </Link>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-ink">
                    {categories.map((c, i) => {
                        const Icon = CAT_ICONS[c.slug] || Printer;
                        return (
                            <Link
                                key={c.id}
                                to={`/loja/${c.slug}`}
                                className={`relative bg-white p-8 hover:bg-ink hover:text-white transition-colors group ${
                                    i % 3 !== 2 ? "lg:border-r-2 border-ink" : ""
                                } ${i < categories.length - (categories.length % 3 || 3) ? "lg:border-b-2" : ""} ${
                                    i % 2 === 0 ? "md:border-r-2 md:border-ink" : ""
                                }`}
                                data-testid={`category-${c.slug}`}
                            >
                                <Icon size={36} className="text-magenta group-hover:text-magenta" />
                                <h3 className="font-display font-medium text-2xl mt-6">{c.name}</h3>
                                <p className="text-sm text-ink/70 group-hover:text-white/70 mt-2 line-clamp-2">
                                    {c.description}
                                </p>
                                <div className="mt-6 flex items-center gap-2 label-mono">
                                    Ver categoria <ArrowRight size={14} />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* FEATURED */}
            <section className="bg-muted border-y-2 border-ink py-16 md:py-24" data-testid="featured-section">
                <div className="container-edge">
                    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                        <div>
                            <div className="label-mono text-magenta">02 — Destaques</div>
                            <h2 className="font-sans font-light text-4xl md:text-5xl tracking-tight mt-2">
                                Os preferidos
                                <br />
                                dos nossos clientes.
                            </h2>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {featured.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="container-edge py-16 md:py-24">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-ink">
                    {[
                        { icon: Truck, t: "Envio em 24h", s: "Despacho no próprio dia para encomendas até às 16h." },
                        { icon: ShieldCheck, t: "Garantia premium", s: "Até 24 meses em consumíveis e equipamentos." },
                        { icon: Leaf, t: "Economia circular", s: "Recondicionados certificados e consumíveis sustentáveis." },
                        { icon: Cpu, t: "Suporte técnico", s: "Técnicos certificados para empresas e particulares." },
                    ].map(({ icon: Icon, t, s }, i) => (
                        <div
                            key={t}
                            className={`p-8 ${i < 3 ? "lg:border-r-2 border-ink" : ""} ${
                                i % 2 === 0 ? "md:border-r-2 md:border-ink" : ""
                            } ${i < 2 ? "md:border-b-2 lg:border-b-0 border-ink" : ""}`}
                        >
                            <Icon className="text-magenta" size={28} />
                            <div className="font-display font-bold text-xl mt-4">{t}</div>
                            <div className="text-sm text-ink/70 mt-2">{s}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA B2B */}
            <section className="bg-ink text-white">
                <div className="container-edge py-16 md:py-24 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="label-mono text-magenta">03 — Empresas</div>
                        <h2 className="font-sans font-light text-4xl md:text-5xl tracking-tight mt-2">
                            Soluções B2B
                            <br />
                            <span className="text-magenta">com vantagens reais.</span>
                        </h2>
                        <p className="mt-6 text-white/70 max-w-lg">
                            Descontos por volume, contas empresariais com fatura mensal, contratos
                            de manutenção e fornecimento contínuo de consumíveis.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-0 border-2 border-magenta">
                        {[
                            { v: "−25%", l: "Desconto B2B" },
                            { v: "30 dias", l: "Pagamento" },
                            { v: "Dedicado", l: "Gestor de conta" },
                            { v: "Contrato", l: "Manutenção" },
                        ].map((s, i) => (
                            <div
                                key={s.l}
                                className={`p-8 ${i % 2 === 0 ? "sm:border-r-2 border-magenta" : ""} ${
                                    i < 2 ? "border-b-2 border-magenta" : ""
                                }`}
                            >
                                <div className="font-display font-medium text-3xl">{s.v}</div>
                                <div className="label-mono text-white/50 mt-2">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <NewsletterSignup />
        </div>
    );
}
